import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { usePermissionGuardRedirect } from "../../../hooks/usePermissionGuard";
import useLinkedProgrammes from "../../../hooks/useLinkedProgrammes";
import ProgressCard from "../../library/ProgressCard";
import LinkProgrammesModal from "./linkedProgrammes/LinkProgrammesModal";
import LinkedProgrammesTable from "./linkedProgrammes/LinkedProgrammesTable";
import LinkedProgrammeBulkActions from "./linkedProgrammes/LinkedProgrammeBulkActions";
import VersionAssignmentModal from "./linkedProgrammes/VersionAssignmentModal";
import ConfirmationModal from "./linkedProgrammes/ConfirmationModal";
import { getProgressLabel } from "../../../utils/LinkedProgrammeUtils";
import {
  StyledCard,
  StyledProgressContainer,
  StyledBulkActionSpacer,
} from "./LinkedProgrammes.styles";

/**
 * Linked-programmes table: progress bar, filters/toolbar, bulk actions, and the
 * version-assignment/grade-lock confirmation modals.
 * @param {object} gradeLockLoading Grade lock/unlock in-flight state, keyed by row id/lockAll/unlockAll.
 * @param {Array<object>} linkedQualifications Linked programme (qualification) rows.
 * @param {object} updateData Trigger object bumped to force a table refresh.
 * @param {string|number} [versionId] When set (reached via a specific version's own nav,
 *   e.g. "versions/linked_programmes"), pre-selects that version in the Version filter as a
 *   starting point. The filter itself stays visible and clearable, per AP-1211 AC10.
 * Also: detail, QAview, isVersionEnabled, id, setGradeLockLoading, setLinkedQualifications,
 *   setLinkedQualificationsLoading, setUpdateData, linkProgrammeOpen, onCloseLinkProgramme
 *   (self-explanatory).
 */
function LinkedProgrammes({
  detail,
  QAview,
  isVersionEnabled,
  id,
  versionId,
  gradeLockLoading,
  setGradeLockLoading,
  linkedQualifications,
  setLinkedQualifications,
  setLinkedQualificationsLoading,
  updateData,
  setUpdateData,
  linkProgrammeOpen = false,
  onCloseLinkProgramme,
}) {
  usePermissionGuardRedirect(["matpview"]);

  const bulkActionsBarRef = useRef(null);
  const [bulkActionsBarHeight, setBulkActionsBarHeight] = useState(0);

  const hook = useLinkedProgrammes({
    detail,
    QAview,
    isVersionEnabled,
    id,
    versionId,
    gradeLockLoading,
    setGradeLockLoading,
    linkedQualifications,
    setLinkedQualifications,
    updateData,
    setUpdateData,
  });

  const closeConfirmation = () => {
    hook.setConfirmation(null);
  };

  const isVersionConfirmation =
    hook.confirmation?.type === "setVersion";

  const hasSelection = hook.selectedIds.length > 0;

  useEffect(() => {
    if (setLinkedQualificationsLoading) {
      setLinkedQualificationsLoading(hook.loading);
    }
  }, [hook.loading, setLinkedQualificationsLoading]);

  useLayoutEffect(() => {
    const barNode = bulkActionsBarRef.current;

    if (!hasSelection || !barNode) {
      return undefined;
    }

    const updateHeight = () => {
      setBulkActionsBarHeight(barNode.getBoundingClientRect().height);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(barNode);

    return () => resizeObserver.disconnect();
  }, [hasSelection]);

  return (
    <>
      {hook.progress && (
        <StyledProgressContainer>
          <ProgressCard
            label={getProgressLabel(
              hook.progress.type
            )}
            current={hook.progress.current}
            total={hook.progress.total}
            unit="Programmes"
            onCancel={() => {
              hook.cancelRemainingRef.current =
                true;
            }}
            cancelText="Cancel remaining"
          />
        </StyledProgressContainer>
      )}

      <StyledCard
        className="career-links-table"
        aria-busy={
          hook.loading || undefined
        }
      >
        <LinkedProgrammesTable
          hook={hook}
          id={id}
          detail={detail}
        />
      </StyledCard>

      <StyledBulkActionSpacer
        $active={hasSelection}
        $height={bulkActionsBarHeight}
      />

      <LinkedProgrammeBulkActions
        barRef={bulkActionsBarRef}
        selectedIds={hook.selectedIds}
        QAview={QAview}
        isVersionEnabled={isVersionEnabled}
        progress={hook.progress}
        versionOptions={hook.versionOptions}
        setSetVersionValue={
          hook.setSetVersionValue
        }
        startConfirmation={
          hook.startConfirmation
        }
      />

      <VersionAssignmentModal
        open={
          Boolean(hook.confirmation) &&
          isVersionConfirmation
        }
        copy={hook.confirmCopy}
        versionValue={hook.setVersionValue}
        onClose={closeConfirmation}
        onConfirm={hook.runConfirmedAction}
      />

      <ConfirmationModal
        open={
          Boolean(hook.confirmation) &&
          !isVersionConfirmation
        }
        copy={hook.confirmCopy}
        onClose={closeConfirmation}
        onConfirm={hook.runConfirmedAction}
      />

      {linkProgrammeOpen && (
        <LinkProgrammesModal
          pmcId={id}
          netsuiteProductId={detail?.netsuite_product}
          refreshSignal={hook.refreshKey}
          onLinked={hook.refresh}
          onClose={onCloseLinkProgramme}
        />
      )}
    </>
  );
}

export default LinkedProgrammes;
